import { DialogType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { AddExternalSiteUsersDialog } from '../../app/users/AddExternalSiteUsersDialog';
import { IAuthenticationProviderSelectorModel } from '../directives/tbAuthenticationProviderSelector';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { EmailValidationHelper } from '../helpers/EmailValidationHelper';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { L10n } from '../L10n/L10n';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteUsers } from '../resources/SiteUsers';
import { IUserActionResult, UserActionResultCode } from '../ServerApi/types/IUserActionResult';
import { AddUsersOptionsAction, UserAddedEvent } from './AddUsersOptionsAction';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ModalSize } from './ModalService';
import { ModalInstance, ReactModalService } from './ReactModalService';
import { ServerService } from './ServerService';
import { ToasterService } from './ToasterService';
import { UserActionNotification, UserActionNotificationParams } from './UserActionNotification';

export interface IAddExternalUsersDialogScope extends ISiteRolePickerMenuScope {
  authenticationProviderSelectorModel: IAuthenticationProviderSelectorModel;

  input: {
    text: string;
  };

  siteRole: string;

  userQuotaMessage: string;

  invalidEmails?: string[];
}

const serverAndSiteUsersDialogStyle: React.CSSProperties = {
  width: 'auto',
  height: 'auto',
  paddingTop: '18px',
  paddingBottom: '12px',
  paddingLeft: '12px',
  paddingRight: '12px'
};

const splitEmails = (emailText: string): string[] => _.chain(emailText.split(/[;,\r\n]+/))
                                                    .map((email: string) => email.trim())
                                                    .compact() // Remove empty strings
                                                    .unique()  // Remove dupes
                                                    .value();

export class AddExternalUsersAction {

  public static $inject = [
    '$translate',
    '$q',
    'AngularContextFactory',
    'ConfirmActionDialog',
    'ToasterService',
    'SiteUsers',
    'ServerService',
    'SiteRoleOptionsResource',
    'AddUsersOptionsAction',
    'UserActionNotification'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private AngularContextFactory: AngularContextFactory,
    private ConfirmActionDialog: ConfirmActionDialog,
    private Toaster: ToasterService,
    private SiteUsers: SiteUsers,
    private Server: ServerService,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private AddUsersOptionsAction: AddUsersOptionsAction,
    private userActionNotification: UserActionNotification
  ) {
  }

  public showAddExternalUsersToSiteDialog(dialogScope: ng.IScope, availableQuota: number): ng.IPromise<any> {

    const addUserScope = this.configureScope(dialogScope);

    if (FeatureFlags.addExternalSiteUsersDialogReactEnabled) {
      let modalInstance: ModalInstance;
      const dialogProps = {
        dismiss: () => modalInstance && modalInstance.dismiss(),
        close: (result: any) => modalInstance && modalInstance.close(result),
        emitUserAddedEvent: () => dialogScope.$emit(UserAddedEvent),
        angularContext: this.AngularContextFactory.create(addUserScope),
        availableQuota: availableQuota
      };

      modalInstance = ReactModalService.open({
        type: DialogType.Uber,
        dialogContent: React.createElement(AddExternalSiteUsersDialog, dialogProps),
        testId: 'addActiveDirectorySiteUsersDialog',
        showCloseButton: true,
        dialogStyle: serverAndSiteUsersDialogStyle,
        shouldNotSetDefaultWidth: true
      });

      return modalInstance.result;
    }

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: addUserScope,
      bodyTemplateUrl: 'confirmActionDialogAddExternalUsers.html',
      title: this.$translate.instant('addExternalUsersTitle'),
      actionName: this.$translate.instant('addUsersDialogSubmit'),
      action: () => {
        const emails = splitEmails(addUserScope.input.text);
        addUserScope.invalidEmails = EmailValidationHelper.getInvalidEmails(emails);

        if(addUserScope.invalidEmails.length > 0) return this.$q.reject();

        return this.SiteUsers.importExternalUsers(emails, addUserScope.siteRole, addUserScope.authenticationProviderSelectorModel.authenticationProvider.id);
      },
      size: ModalSize.fitToContent
    });

    dialogInstance.result.then((result: IUserActionResult) => {
      this.handleImportResult(result, addUserScope);
    })
    .finally(() => addUserScope.$destroy());

    return dialogInstance.result;
  }

  public configureScope(dialogScope: ng.IScope): IAddExternalUsersDialogScope {
    const addUserScope = <IAddExternalUsersDialogScope> dialogScope.$new();

    addUserScope.input = {
      text: ''
    };
    addUserScope.authenticationProviderSelectorModel = this.AddUsersOptionsAction.getInitialAuthenticationProviderSelectorModel();

    addUserScope.siteRole = DefaultSiteRole;
    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      addUserScope.$apply(() => {
        addUserScope.siteRole = roleOption.role; // also set $scope.siteRole so original behavior is unaltered
        SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.$translate, this.Server, this.siteRoleOptionsResource, addUserScope.siteRole, onSiteRoleSelected, true);
      });
    };

    SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.$translate, this.Server, this.siteRoleOptionsResource, addUserScope.siteRole, onSiteRoleSelected, true);

    this.SiteUsers.getRemainingUserQuota().then((quota: number) => {
      addUserScope.userQuotaMessage = this.isQuotaEnabled(quota)
                                      ? this.$translate.instant('availableQuota', { NUM_USERS: quota }, L10n.Interpolations.Count)
                                      : null;
    });

    return addUserScope;
  }

  public isQuotaEnabled(availableQuota: number): boolean {
    if (this.Server.isServerDomainFamilyExternal() && this.Server.hasSubscriptionTieredLicensing()) {
      return false;
    }
    return _.isNumber(availableQuota);
  }

  private handleImportResult(result: IUserActionResult, addUserScope: IAddExternalUsersDialogScope) {
    //TODO: Replace with generic user error result handling
    const params: UserActionNotificationParams = {
      users: Set.fromIdItemArray(result.users),
      results: result.results,
      errors: result.errors,
      successCode: UserActionResultCode.siteUserCreated,
      siteRole: addUserScope.siteRole
    };
    this.userActionNotification.notify(params);
    addUserScope.$emit(UserAddedEvent);
  }

}

Module.instance().service('AddExternalUsersAction', AddExternalUsersAction);
